import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BarcodeDisplay from './BarcodeDisplay'; // Assuming this component exists
import CountdownTimer from './CountdownTimer';

const WalletComponent = ({ userDetails, amount, onClose }) => {
  const [selectedWallet, setSelectedWallet] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [isPaymentConfirmed, setIsPaymentConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [copiedMessage, setCopiedMessage] = useState(false);

  const fee = 3; // Transaction fee displayed to user
  const numericAmount = isNaN(parseFloat(amount)) ? 0 : parseFloat(amount);
  const totalAmount = numericAmount + fee;

  // Fetch random wallet on mount
  useEffect(() => {
    const fetchRandomWallet = async () => {
      try {
        const response = await axios.get('https://sharedvision-partners.online/v1/API/random_wallet.php', { timeout: 10000 });
        if (response.data && response.data.walletAddress) {
          setSelectedWallet(response.data.walletAddress);
          setQrCodeUrl(response.data.qrCodeUrl || '');
        } else {
          setErrorMessage('Failed to fetch wallet address.');
        }
      } catch (error) {
        console.error('Error fetching wallet:', error);
        setErrorMessage('Unable to fetch wallet address. Please try again later.');
      }
    };
    fetchRandomWallet();
  }, []);

  // Confirm payment (simulate sending topup request)
  const handleConfirmPayment = async () => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const response = await axios.post(
        'https://sharedvision-partners.online/v1/API/topup.php',
        {
          email: userDetails?.email,
          amount: totalAmount,
        },
        { timeout: 10000 }
      );

      if (response.data.status === 'success') {
        setIsPaymentConfirmed(true);
      } else {
        throw new Error(response.data.message || 'Failed to process payment');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      setErrorMessage('Payment confirmation failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Poll payment status every 30 seconds
  useEffect(() => {
    if (!selectedWallet) return;
    if (isPaymentConfirmed) {
      // If payment confirmed, update user balance
      const updateBalance = async () => {
        try {
          const response = await axios.post('https://sharedvision-partners.online/v1/API/update_balance.php', {
            email: userDetails.email,
            amount: totalAmount, // total amount paid
          });

          if (response.data.status === 'success') {
            console.log('User balance updated:', response.data.newBalance);
          } else {
            console.error('Failed to update balance:', response.data.message);
          }
        } catch (error) {
          console.error('Error updating balance:', error);
        }
      };

      updateBalance();
      return; // skip polling once confirmed and balance updated
    }

    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`https://sharedvision-partners.online/v1/API/check_payments.php?address=${selectedWallet}`);
        if (res.data.status === 'success' && res.data.newPayments && res.data.newPayments.length > 0) {
          // Payment received - update UI & stop polling
          setIsPaymentConfirmed(true);
          clearInterval(interval);
        }
      } catch (error) {
        console.error('Payment check failed:', error);
      }
    }, 30000); // 30 seconds polling

    return () => clearInterval(interval);
  }, [selectedWallet, isPaymentConfirmed, totalAmount, userDetails.email]);

  // Copy wallet address to clipboard
  const handleCopyAddress = () => {
    if (selectedWallet) {
      navigator.clipboard.writeText(selectedWallet);
      setCopiedMessage(true);
      setTimeout(() => setCopiedMessage(false), 2000);
    }
  };

  return (
    <div className="card mx-auto my-4" style={{ maxWidth: '650px', position: 'relative' }}>
      <div className="card-body text-center">
        <h2 className="card-title mb-4">Payment Information</h2>

        {errorMessage && (
          <div className="alert alert-danger mb-3" role="alert">
            {errorMessage}
          </div>
        )}

        <div className="mb-4">
          <p className="mb-1">
            We impose a mandatory fee of <strong>${fee}</strong> for all transactions.
          </p>
          <p className="mb-2">
            Please send <strong>${totalAmount} USDT (BEP-20)</strong> to the address below:
          </p>

          <h5
            title="Click to copy"
            className="text-break user-select-all"
            style={{ cursor: 'pointer' }}
            onClick={handleCopyAddress}
          >
            {selectedWallet || 'Loading wallet address...'}
          </h5>

          <button
            className="btn btn-outline-secondary btn-sm mt-2"
            onClick={handleCopyAddress}
            disabled={!selectedWallet}
            aria-label="Copy wallet address"
          >
            📋 Copy Address
          </button>

          {copiedMessage && <div className="text-success mt-2 fw-bold">✅ Address copied!</div>}
        </div>

        <p>
          <strong>You can also Pay via Trust Wallet</strong>
        </p>
        <a
          href={`https://link.trustwallet.com/send?asset=c20000714_t0x55d398326f99059fF775485246999027B3197955&address=${selectedWallet}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            src="https://altcoinsbox.com/wp-content/uploads/2023/03/trust-wallet-logo-600x600.webp"
            alt="Trust Wallet Logo"
            style={{ width: '70px', height: '70px' }}
            className="mb-4"
          />
        </a>

        <CountdownTimer
          initialSeconds={900}
          onTimeout={onClose}
          onClosingStart={() => setIsClosing(true)}
        />

        {isClosing && (
          <div className="mt-3">
            <p>🔒 Closing wallet...</p>
            <div className="spinner-border text-primary" role="status" />
          </div>
        )}

        {isPaymentConfirmed ? (
          <div className="alert alert-info mt-4" role="alert">
            <h5 className="mb-2">Payment received and balance updated</h5>
            <p>Thank you! Your balance has been updated successfully.</p>
            <button className="btn btn-primary mt-2" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <div className="mt-3">
            {isLoading ? (
              <p>🔄 Processing payment... Please wait.</p>
            ) : (
              <button
                className="btn btn-success"
                onClick={handleConfirmPayment}
                disabled={isLoading || !selectedWallet}
              >
                Confirm Payment
              </button>
            )}
          </div>
        )}

        {/* QR Code */}
        <div className="mt-4">
          {qrCodeUrl ? (
            <img
              src={qrCodeUrl}
              alt="Wallet QR Code"
              style={{ maxWidth: '200px', maxHeight: '200px' }}
              className="mx-auto"
            />
          ) : (
            selectedWallet && <BarcodeDisplay value={selectedWallet} />
          )}
        </div>
      </div>
    </div>
  );
};

export default WalletComponent;
